import { omit, sortBy } from 'lodash';
import BPromise from 'bluebird';

import {
  DbAttachment,
  DbCommit,
  DbFile,
  DbFileVersion,
  DbFolder,
  DbProject,
  DbTenderFolder,
  DbTimestamp,
} from '~src/types/db';
import { DB_STATE } from '~src/types/enums';
import { appLog } from '~src/utils/log';
import { apiCweb } from './api-cweb';

import _ from 'lodash';
import { LoadArchiveSettingsParams, Tag } from './archiveSettings';
import { SimpleTag } from '~components/popups-modals/EditFile.modal';
import {
  getStorageAdminDownload,
  getStorageTimestamp,
} from '~src/utils/sessionstorageQueries';

export const idOrNull = (bsId?: number | null): string | null =>
  bsId ? String(bsId) : null;

interface CreateFileParams {
  data: Pick<
    DbFile,
    'title' | 'project_id' | 'folder_id' | 'description' | 'state'
  > &
    Partial<DbFile>;
  commit_id?: DbCommit['id'];
}
/**
 * Create db file entity (not actual upload)
 */
export const createFile = async ({
  data,
  commit_id,
}: CreateFileParams): Promise<DbFile> => {
  appLog('createFile ->', { data });
  const res = await apiCweb.post<DbFile>(
    `/projects/${data.project_id}/versioned_files?commit_id=${commit_id}`,
    data
  );
  return res.data;
};

interface EditFileParams {
  data: DbFile;
}
/**
 * Edit file entry
 */
export const editFile = async ({ data }: EditFileParams): Promise<DbFile> => {
  appLog('editFile ->', { data });
  const res = await apiCweb.put<DbFile>(
    `/projects/${data.project_id}/versioned_files/${data.id}`,
    omit(data, ['versions'])
  );
  return res.data;
};

interface DeleteFileParams {
  projectId: string;
  file: DbFile;
}
/**
 * Delete file entry
 */
export const deleteFile = async ({
  projectId,
  file,
}: DeleteFileParams): Promise<DbFile> => {
  appLog('deleteFile ->', { projectId, file });
  const res = await apiCweb.delete<DbFile>(
    `/projects/${projectId}/versioned_files/${file.id}`
  );
  return res.data;
};

interface DeleteAttachmentParams {
  projectId: string;
  tenderId?: string;
  attachment: DbAttachment;
}

export const deleteAttachment = async ({
  projectId,
  tenderId,
  attachment,
}: DeleteAttachmentParams): Promise<DbAttachment> => {
  appLog('deleteAttachment ->', { projectId, tenderId, attachment });
  // const attachmentData = attachment.files ? attachment.files[0] : attachment;
  // console.log('attachmentData', attachmentData);
  const res = await apiCweb.delete<DbAttachment>(
    `/projects/${projectId}/tenders/${tenderId}/attachments/${attachment.id}`
    // `/projects/${projectId}/tenders/5600/attachments/5612`
  );
  return res.data;
};

export const restoreAttachment = async ({
  projectId,
  tenderId,
  attachment,
}: DeleteAttachmentParams): Promise<DbAttachment> => {
  appLog('restoreAttachment ->', { projectId, attachment });
  const res = await apiCweb.put<DbAttachment>(
    `/projects/${projectId}/tenders/${tenderId}/attachments/${attachment.id}?action=reactivate`,
    {}
  );
  return res.data;
};

export interface LoadFilesParams {
  projectId: string;
  folderId?: string | null;
  withVersions?: boolean;
  afterDate?: DbTimestamp;
  limit?: number;
  favourites?: boolean;
  localSortBy?: string[] | null;
  state?: DB_STATE[];
  showDeleted?: boolean;
  before?: number;
  search_name?: string;
  search_description?: string;
  search_revision?: string;
  oldFilesImplementation?: boolean; //GET /versioned_files with ?favorites param set to true and then GET /files with ?versioned_file_id
  any_tags_of?: string;
  all_tags_of?: string;
}

export const loadFiles = async (
  key: string,
  {
    projectId,
    folderId,
    withVersions,
    afterDate,
    limit,
    favourites,
    localSortBy = ['id'],
    state,
    showDeleted,
    before,
    search_name,
    search_description,
    search_revision,
    oldFilesImplementation = false,
    any_tags_of,
    all_tags_of,
  }: LoadFilesParams
): Promise<DbFile[]> => {
  appLog('loadFiles ->', {
    projectId,
    folderId,
    withVersions,
    afterDate,
    limit,
    favourites,
    localSortBy,
    state,
    showDeleted,
    before,
    search_name,
    search_description,
    search_revision,
    oldFilesImplementation,
    any_tags_of,
    all_tags_of,
  });
  const res = await apiCweb.get<DbFile[]>(
    `/projects/${projectId}/versioned_files`,
    // `root_id` is actually `folder_id`, and behaves differently then `root_id` on `GET /folders`
    {
      params: {
        order_by: 'updated_at',
        order: 'desc',
        root_id: folderId,
        take: limit,
        favorites: favourites,
        after: afterDate,
        state: state?.toString(),
        show_deleted: showDeleted,
        before: before,
        search_name,
        search_description,
        search_revision,
        any_tags_of,
        all_tags_of,
      },
    }
  );

  if (!withVersions) return res.data;

  if (oldFilesImplementation) {
    // attach file versions
    await BPromise.map(
      res.data,
      async (file) => {
        file.versions = await loadFileVersions('versions', {
          projectId,
          folderId,
          versionedFileId: file.id,
          before: before,
        });
      },
      { concurrency: 5 }
    );
    if (localSortBy) return sortBy(res.data, localSortBy);
    return res.data;
  }

  const fileVersions = await loadFileVersions('versions', {
    projectId,
    folderId,
    before,
  });

  const files = res.data.map((item) => ({
    ...item,
    versions: _.uniqBy(
      fileVersions.filter((file) => file.versioned_file_id === item.id),
      (e) => e.id
    ),
  }));

  // if (localSortBy) return sortBy(res.data, localSortBy);
  if (localSortBy) return sortBy(files, localSortBy);
  return files;
};

interface LoadFileVersionsParams {
  projectId: string;
  folderId?: string | null;
  versionedFileId?: string | number;
  before?: number;
  after?: number;
}
export const loadFileVersions = async (
  key: string,
  {
    projectId,
    versionedFileId,
    before,
    folderId,
    after,
  }: LoadFileVersionsParams
): Promise<DbFileVersion[]> => {
  appLog('loadFileVersions ->', {
    projectId,
    versionedFileId,
    folderId,
    before,
    after,
  });
  const res = await apiCweb.get<DbFileVersion[]>(`/files`, {
    params: {
      project_id: projectId,
      versioned_file_id: versionedFileId,
      folder_id: folderId,
      before,
      after,
    },
  });

  // sortBt 'created_at' DESC
  return sortBy(res.data, ['created_at']).reverse();
};

interface newFileType {
  file_id: string;
  file_name: string;
  file_size: number;
  folder_id: number;
  using_key: string;
}

type newLoadFilesParams = {
  projectId: number;
  folderId?: number;
  serial: any;
  registration_ids?: number;
  recursive?: boolean;
  before?: number;
  skip?: number;
  take?: number;
};

export const newLoadFiles = async (
  key: string,
  { projectId, folderId, serial = 1 }: newLoadFilesParams
): Promise<newFileType[]> => {
  // appLog('newLoadFiles ->', { projectId, folderId, serial });
  try {
    const timestamp = getStorageTimestamp();
    const adminQuery = getStorageAdminDownload();

    const res = await apiCweb.get<newFileType[]>(
      `/projects/${projectId}/versioned_files/file_download`,
      {
        params: {
          folder_id: folderId,
          serial: serial,
          recursive: true,
          skip: 0,
          ...(timestamp > 0 && {
            before: timestamp,
          }),
          ...(adminQuery && {
            as_admin: adminQuery,
            include_deleted: adminQuery,
          }),
        },
        timeout: 30000,
      }
    );

    // console.log(`newLoadFiles LOG -> ${serial}`, res.data);
    return res.data;
  } catch (error) {
    console.error(`Failed to load files for folder: ${folderId}`, error);
    return [];
  }
};

export const getFilesURLs = async (projectId: string, using_keys: string[]) => {
  try {
    const res = await apiCweb.post(
      `/projects/${projectId}/versioned_files/batch_download_urls`,
      { using_keys },
      { timeout: 30000 }
    );

    // console.log(`getFilesURLs LOG ->`, res.data);
    return res.data;
  } catch (error) {
    return [];
  }
};

// Tender Folder's Download
export const newTenderFolderDownload = async (
  key: string,
  { projectId, tenderId, rootFolderId, folderId }
): Promise<newFileType[]> => {
  appLog('newTenderFolderDownload ->', {
    projectId,
    tenderId,
    rootFolderId,
    folderId,
  });
  try {
    // const timestamp = getStorageTimestamp();
    // const adminQuery = getStorageAdminDownload();

    const res = await apiCweb.get(
      `/projects/${projectId}/tenders/${tenderId}/tender_folders/${rootFolderId}/folders/${folderId}/versioned_files/file_download`,
      {
        params: {
          recursive: true,
          skip: 0,
          // ...(timestamp > 0 && {
          //   before: timestamp,
          // }),
          // ...(adminQuery && {
          //   as_admin: adminQuery,
          //   include_deleted: adminQuery,
          // }),
        },
        timeout: 30000,
      }
    );

    // console.log(`newTenderFolderDownload LOG ->`, res.data);
    return res.data;
  } catch (error) {
    console.error(`Failed to load files for folder: ${folderId}`, error);
    return [];
  }
};

export const getTenderFolderFilesURLs = async (
  projectId: string,
  tenderId: string,
  using_keys: string[]
) => {
  try {
    const res = await apiCweb.post(
      `/projects/${projectId}/tenders/${tenderId}/tender_folders/batch_download_urls`,
      { using_keys },
      { timeout: 30000 }
    );

    // console.log(`getTenderFolderFilesURLs LOG ->`, res.data);
    return res.data;
  } catch (error) {
    return [];
  }
};

export const newSingleFileDownload = async (
  key: string,
  { projectId, registration_ids, serial = 1 }: newLoadFilesParams
): Promise<newFileType[]> => {
  // appLog('newSingleFileDownload ->', { projectId, registration_ids });
  try {
    const timestamp = getStorageTimestamp();
    const adminQuery = getStorageAdminDownload();
    const res = await apiCweb.get<newFileType[]>(
      `/projects/${projectId}/versioned_files/file_download`,
      {
        params: {
          registration_ids: registration_ids,
          serial: serial,
          recursive: true,
          skip: 0,
          ...(timestamp > 0 && {
            before: timestamp,
          }),
          ...(adminQuery && {
            as_admin: adminQuery,
            include_deleted: adminQuery,
          }),
        },
        timeout: 30000,
      }
    );

    // console.log(`newSingleFileDownload LOG -> ${serial}`, res.data);
    return res.data;
  } catch (error) {
    console.error(`Failed to load file: ${registration_ids}`, error);
    return [];
  }
};

interface AddToFavoriteFilesParams {
  data: DbFile;
}
/**
 * Add to favorite files
 */
export const addToFavoriteFiles = async ({
  data,
}: AddToFavoriteFilesParams): Promise<DbFile> => {
  appLog('addToFavoriteFiles ->', { data });
  const res = await apiCweb.post<DbFile>(
    `/projects/${data.project_id}/favorites/versioned_files/${data.id}`,
    {}
  );
  return res.data;
};

interface RemoveFromFavoriteFilesParams {
  data: DbFile;
}
/**
 * Remove from favorite files
 */
export const removeFromFavoriteFiles = async ({
  data,
}: RemoveFromFavoriteFilesParams): Promise<DbFile> => {
  appLog('removeFromFavoriteFiles ->', { data });
  const res = await apiCweb.delete<DbFile>(
    `/projects/${data.project_id}/favorites/versioned_files/${data.id}`
  );
  return res.data;
};

interface ToggleVFileNotifyParams {
  projectId: DbProject['id'];
  versionedFileId: DbFile['id'];
  notify: boolean;
}

export const toggleVFileNotify = async ({
  projectId,
  versionedFileId,
  notify,
}: ToggleVFileNotifyParams) => {
  const res = await apiCweb.put(
    `/projects/${projectId}/versioned_files/${versionedFileId}/notify`,
    { is_notify: notify }
  );
  return res.data;
};
export interface ChangeFileStateParam {
  projectId: DbProject['id'];
  registrationId: DbFileVersion['id'];
  stateValue: DB_STATE;
  commitId?: DbCommit['id'];
}

export const changeFileState = async (
  // key: string,
  { projectId, registrationId, stateValue, commitId }: ChangeFileStateParam
) => {
  appLog('changinFileState ->', { projectId, registrationId, stateValue });
  const res = await apiCweb.put(`/projects/${projectId}/versioned_files`, {
    registration_id: registrationId,
    state_value: stateValue,
    commit_id: commitId,
  });
  return res.data;
};

export interface ChangeFilesStateParam {
  projectId: DbProject['id'];
  registrationsId: (number | string)[];
  stateValue: DB_STATE;
  commit?: DbCommit;
}

export const changeFilesState = async ({
  projectId,
  registrationsId,
  stateValue,
  commit,
}: ChangeFilesStateParam) => {
  appLog('changinFilesState ->', { projectId, registrationsId, stateValue });

  const res = await apiCweb.put(
    `/projects/${projectId}/versioned_files?registration_id=${registrationsId}&state_value=${stateValue}`,

    {
      // registration_id: registrationsId,
      // state_value: stateValue,
      commit_id: commit?.id,
    }
  );
  return res.data;
};

type ChangeAttachmentsStateProps = {
  attachmentIds: string[];
  projectId: DbProject['id'];
  tenderId: string;
};

export const changeAttachmentsState = async ({
  projectId,
  attachmentIds,
  tenderId,
}: ChangeAttachmentsStateProps) => {
  appLog('changeAttachmentsState ->', { projectId, attachmentIds, tenderId });

  await Promise.all(
    attachmentIds.map((attachmentId) =>
      apiCweb.put(
        `/projects/${projectId}/tenders/${tenderId}/attachments/${attachmentId}`,
        {},
        {
          params: {
            action: 'reactivate',
          },
        }
      )
    )
  );
};

interface MoveFilesParam {
  projectId: DbProject['id'];
  files: DbFile['id'][];
  // fileId: number;
  folderId: DbFolder['id'];
  commit?: DbCommit;
}

export const moveFiles = async ({
  projectId,
  files,
  folderId,
  commit,
}: MoveFilesParam) => {
  appLog('moveFiles -> ', { projectId, files, folderId });

  const res = await apiCweb.put(
    `projects/${projectId}/versioned_files?move=${files}&value=${folderId}&commit_id=${commit?.id}`,
    {}
  );
  return res.data;
};

interface CopyFilesParams {
  projectId: DbProject['id'];
  files: DbFile['id'][];
  folderId: DbFolder['id'];
  commit?: DbCommit;
}

export const copyFiles = async ({
  projectId,
  files,
  folderId,
  commit,
}: CopyFilesParams) => {
  const res = await apiCweb.post(
    `projects/${projectId}/versioned_files?copy=${files}&value=${folderId}&commit_id=${commit?.id}`,
    {}
  );

  return res.data;
};

export interface RequestFileLocation {
  fileId: string;
  projectId: number;
  versionedFileId: number;
}

export const requestFileLocation = async ({
  fileId,
  projectId,
  versionedFileId,
}: RequestFileLocation) => {
  const res = await apiCweb.get<{ location: string }>(`/files/${fileId}`, {
    params: {
      project_id: projectId,
      versioned_file_id: versionedFileId,
    },
  });

  return res.data.location;
};

export const loadFileVersionsLocation = async ({
  projectId,
  versionedFileId,
}: RequestFileLocation) => {
  const res = await apiCweb.head(
    `projects/${projectId}/versioned_files/${versionedFileId}`
  );

  return res.data;
};

export const loadFileTags = async (
  key: string,
  versionedFileId: number,
  { projectId }: Tag
): Promise<SimpleTag[]> => {
  const res = await apiCweb.get(
    `projects/${projectId}/versioned_files/${versionedFileId}/tags`
  );

  return res.data;
};

interface AddTagParams {
  projectId: string;
  id: string;
  versionedFileId: number;
}

export const addTagFile = async ({
  projectId,
  versionedFileId,
  id,
}: AddTagParams) => {
  appLog('addTagFile ->', { projectId });
  const res = await apiCweb.post(
    `projects/${projectId}/versioned_files/${versionedFileId}/tags?tag_ids=${id}`,
    {}
  );
  return res.data;
};

export const deleteTagFile = async ({
  projectId,
  versionedFileId,
  id,
}: AddTagParams) => {
  appLog('deleteTagFile ->', { projectId });
  const res = await apiCweb.delete(
    `projects/${projectId}/versioned_files/${versionedFileId}/tags?tag_ids=${id}`,
    {}
  );
  return res.data;
};

export interface PrimarySVFRenderResponse {
  versioned_file_title: string;
  versioned_file_folder_id: number;
  file_guid: string;
  file_serial_number: number;
  autodesk_object_id: string;
}

export const getPrimarySVFRender = async (
  projectId: number
): Promise<PrimarySVFRenderResponse | null> => {
  appLog('getPrimarySVFRender #1 ->', { projectId });
  try {
    const res = await apiCweb.get(
      `project_settings/${projectId}/primary_svf_render`,
      {
        //removed redirect to 404 if no preview file
        validateStatus: (status: number) => {
          return (status >= 200 && status < 300) || status === 404;
        },
      }
    );
    appLog('getPrimarySVFRender #2 ->', res.data);
    return res.data;
  } catch (err) {
    appLog('[getPrimarySVFRender] error', err);
    throw err;
  }
};

export interface PrimarySVFRenderLocationResponse {
  access_token: string;
  urn: string;
}

export const getPrimarySVFRenderLocation = async (
  projectId: number
): Promise<PrimarySVFRenderLocationResponse | null> => {
  appLog('getPrimarySVFRenderLocation #1 ->', { projectId });

  try {
    const res = await apiCweb.get(
      `project_settings/${projectId}/primary_svf_render/location`
    );

    appLog('getPrimarySVFRenderLocation #2 ->', res.data);
    return res.data;
  } catch (err) {
    appLog('[getPrimarySVFRenderLocation] error', err);
    throw err;
  }
};

export const deletePrimarySVFRender = async (projectId: number) => {
  appLog('deletePrimarySVFRender #1 ->', { projectId });

  try {
    const res = await apiCweb.delete(
      `project_settings/${projectId}/primary_svf_render`
    );

    appLog('deletePrimarySVFRender #2 ->', res.data);
    return res.data;
  } catch (err) {
    appLog('[deletePrimarySVFRender] error:', err);
  }
};

export const setPrimarySVFRender = async (
  projectId: number,
  folderId: number,
  versionedFileId: number,
  asAdmin?: boolean
) => {
  appLog('setPrimarySVFRender #1 ->', {
    projectId,
    folderId,
    versionedFileId,
    asAdmin,
  });

  try {
    const res = await apiCweb.put(
      `projects/${projectId}/folders/${folderId}/versioned_files/${versionedFileId}/primary_svf_render`,
      {},
      {
        params: {
          as_admin: asAdmin ? true : undefined,
        },
      }
    );

    appLog('setPrimarySVFRender #2 ->', res.status);
    return res.status;
  } catch (err) {
    appLog('[setPrimarySVFRender] error:', err);
    throw err;
  }
};

export interface UpdateTenderAttachmentParams {
  projectId: number | string;
  tenderId: number | string;
  fileGuid: string;
  data: {
    name: string;
    description?: string;
    version?: number | null;
  };
}

export async function updateTenderAttachment({
  projectId,
  tenderId,
  fileGuid,
  data,
}: UpdateTenderAttachmentParams): Promise<void> {
  const res = await apiCweb.put(
    `projects/${projectId}/tenders/${tenderId}/attachments/${fileGuid}?action=update`,
    data
  );
  return res.data;
}

interface VendorVoucherAttachmentParamsBase {
  shareId: string;
  shareKey: string;
  offerId: number;
  voucherId: string | number;
  fileGuid: string;
}

/**
 * HEAD /v1/tender_shares/{share_id}/offers/{offer_id}/vouchers/{voucher_id}/responses/attachment/{file_guid}/location
 * Returns S3 URL in `location` header
 */
export const getVendorVoucherAttachmentDownloadUrl = async ({
  shareId,
  shareKey,
  offerId,
  voucherId,
  fileGuid,
}: VendorVoucherAttachmentParamsBase): Promise<string> => {
  appLog(`getVendorVoucherAttachmentDownloadUrl #1`, {
    fileGuid,
    voucherId,
    offerId,
    shareKey,
  });

  const res = await apiCweb.head(
    `/tender_shares/${shareId}/offers/${offerId}/vouchers/${voucherId}/responses/attachment/${fileGuid}/location`,
    {
      headers: {
        'Share-Key': shareKey,
      },
    }
  );

  appLog('getVendorVoucherAttachmentDownloadUrl #2 ->', res);
  return res.headers.location;
};

/**
 * HEAD /v1/tender_shares/{share_id}/contracts/{contract_id}/vouchers/{voucher_id}/attachments/{file_guid}/location
 * Returns S3 URL in `location` header
 * Download PROJECT-side attachment from VENDOR side
 */
export const getProjectVoucherAttachmentDownloadUrl = async ({
  shareId,
  shareKey,
  contractId,
  voucherId,
  fileGuid,
}) => {
  appLog(`getProjectVoucherAttachmentDownloadUrl #1`, {
    fileGuid,
    voucherId,
    contractId,
    shareKey,
  });

  const res = await apiCweb.head(
    `/tender_shares/${shareId}/contracts/${contractId}/vouchers/${voucherId}/attachments/${fileGuid}/location`,
    {
      headers: {
        'Share-Key': shareKey,
      },
    }
  );

  appLog('getProjectVoucherAttachmentDownloadUrl #2 ->', res);
  return res.headers.location;
};

/**
 * DELETE /v1/tender_shares/{share_id}/offers/{offer_id}/vouchers/{voucher_id}/responses/attachment/{file_guid}
 */
export const deleteVendorVoucherAttachment = async ({
  shareId,
  shareKey,
  offerId,
  voucherId,
  fileGuid,
}: VendorVoucherAttachmentParamsBase): Promise<void> => {
  appLog(`deleteVendorVoucherAttachment worked`);
  await apiCweb.delete(
    `/tender_shares/${shareId}/offers/${offerId}/vouchers/${voucherId}/responses/attachment/${fileGuid}`,
    {
      headers: {
        'Share-Key': shareKey,
      },
    }
  );
};

// ==========================================
// UNASSIGNED TENDER FOLDERS APIs
// ==========================================

export const newUnassignedTenderFolderDownload = async (
  key: string,
  { projectId, rootFolderId, folderId }
): Promise<newFileType[]> => {
  appLog('newUnassignedTenderFolderDownload ->', {
    projectId,
    rootFolderId,
    folderId,
  });
  try {
    const res = await apiCweb.get<newFileType[]>(
      `/projects/${projectId}/tender_folders/${rootFolderId}/folders/${folderId}/versioned_files/file_download`,
      {
        params: {
          recursive: true,
          skip: 0,
        },
        timeout: 30000,
      }
    );

    appLog(`newUnassignedTenderFolderDownload LOG ->`, res.data);
    return res.data;
  } catch (error) {
    appLog(`Failed to load files for folder: ${folderId}`, error);
    return [];
  }
};

export const loadUnassignedTenderRootFolders = async (
  projectId: string | number
) => {
  appLog('loadUnassignedTenderRootFolders ->', { projectId });
  try {
    const res = await apiCweb.get<DbTenderFolder[]>(
      `/projects/${projectId}/tender_folders`,
      {
        params: { unassigned: true },
      }
    );
    return res.data;
  } catch (error) {
    appLog('loadUnassignedTenderRootFolders error ->', error);
    return [];
  }
};

export const loadUnassignedTenderSubfolders = async (
  projectId: string | number,
  rootFolderId: string | number
) => {
  appLog('loadUnassignedTenderSubfolders ->', { projectId, rootFolderId });
  try {
    const res = await apiCweb.get<DbTenderFolder[]>(
      `/projects/${projectId}/tender_folders/${rootFolderId}/folders`
    );
    return res.data;
  } catch (error) {
    appLog('loadUnassignedTenderSubfolders error ->', error);
    return [];
  }
};
